import { ArrowUpIcon, Loader2Icon } from "lucide-react";

import { Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { useSheetScope } from "@/contexts/SheetScopeContext";
import { useErrorToast } from "@/hooks/useErrorToast";
import { useEnrichProfile } from "@/hooks/mutations/useEnrichProfile";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import {
  EnrichedProfileState,
  EnrichmentInputsState,
  EnrichmentMessageRole,
  EnrichmentResponse,
} from "@/types";

// Keeps "Enriched Profile" state up to date with the latest enrichment response.
const mergeIntoEnrichedProfile = (
  prev: EnrichedProfileState,
  response: EnrichmentResponse,
): EnrichedProfileState => ({
  executiveSummary: response.executiveSummary ?? prev.executiveSummary,
  professionalSummary: response.professionalSummary ?? prev.professionalSummary,
  workExperiences: response.workExperiences ?? prev.workExperiences,
  educations: response.educations ?? prev.educations,
  certifications: response.certifications ?? prev.certifications,
  skills: response.skills ?? prev.skills,
});

export const EnrichmentComposer = ({
  enrichmentInputs,
}: {
  enrichmentInputs: EnrichmentInputsState;
}) => {
  const {
    additionalContext,
    enrichedProfile,
    isEnriching,
    messages,
    prompt,
    referenceCallTranscript,
    selectedStafferCallIds,
    selectedStevieCallIds,
    setEnrichedProfile,
    setIsEnriching,
    setMessages,
    setPrompt,
  } = enrichmentInputs;

  /* Contexts */
  const { candidacyId, companyId, jobId } = useSheetScope();

  /* Database CRUD Operations */
  // Writes
  const enrichProfileMutation = useEnrichProfile();

  /* Misc Hooks */
  const errorToast = useErrorToast();

  /* Helpers */
  const hasEnriched = messages.length > 0;
  const userInput = prompt.trim();
  const isSubmitDisabled =
    isEnriching || (hasEnriched && userInput.length === 0);

  /* Handlers */
  const handleEnrich = () => {
    if (isSubmitDisabled) return;

    mixpanelUtils.trackButtonClick(
      "Run Enrichment",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
      {
        candidacy_id: candidacyId,
        job_id: jobId,
        is_follow_up: hasEnriched,
        has_instructions: userInput.length > 0,
      },
    );

    // Add the recruiter's message to our string of messages
    const nextMessages = userInput
      ? [
          ...messages,
          {
            id: crypto.randomUUID(),
            role: EnrichmentMessageRole.USER,
            content: userInput,
          },
        ]
      : messages;

    setMessages(nextMessages);
    setIsEnriching(true);
    setPrompt("");

    enrichProfileMutation.mutate(
      {
        candidacyId,
        currentCompanyId: companyId,
        currentJobId: jobId,
        enrichmentRequest: {
          messages: nextMessages,
          selectedStafferCallIds: Array.from(selectedStafferCallIds),
          selectedStevieCallIds: Array.from(selectedStevieCallIds),
          referenceCallTranscript,
          additionalContext,
          currentEnrichedProfile: messages.length > 0 ? enrichedProfile : null,
        },
      },
      {
        onSuccess: (response) => {
          // Add the AI assistant's message to our string of messages
          setMessages((prev) => [
            ...prev,
            {
              id: crypto.randomUUID(),
              role: EnrichmentMessageRole.ASSISTANT,
              content: JSON.stringify(response),
            },
          ]);
          setEnrichedProfile((prev) =>
            mergeIntoEnrichedProfile(prev, response),
          );
          setIsEnriching(false);
        },
        onError: () => {
          errorToast({ description: "Enrichment failed. Please try again." });
          setIsEnriching(false);
        },
      },
    );
  };

  /* Render */
  const render = () => {
    return (
      <Column className="shrink-0 gap-2 px-1 pt-3">
        <div className="rounded-3xl bg-gradient-to-br from-amber-200/70 via-pink-200/70 to-indigo-200/70 p-[1.5px] shadow-md">
          <Row className="items-end gap-2 rounded-[calc(1.5rem-1.5px)] bg-white px-4 py-2.5">
            <Textarea
              autoExpand
              className="max-h-[240px] min-h-[28px] resize-none border-0 bg-transparent px-0 py-1 text-sm text-slate-700 shadow-none focus-visible:outline-none focus-visible:ring-0"
              disabled={isEnriching}
              onChange={(e) => {
                setPrompt(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleEnrich();
                }
              }}
              placeholder={
                hasEnriched
                  ? "Ask for any follow-up edits…"
                  : "Add any extra instructions for this candidate (optional)…"
              }
              value={prompt}
            />
            <Button
              className="shrink-0 rounded-full"
              disabled={isSubmitDisabled}
              onClick={handleEnrich}
              size="icon"
              type="button"
            >
              {isEnriching ? (
                <Loader2Icon className="animate-spin" />
              ) : (
                <ArrowUpIcon />
              )}
            </Button>
          </Row>
        </div>
        <Text className="text-center text-xs" variant="muted">
          Enrich can hallucinate. Review output carefully.
        </Text>
      </Column>
    );
  };

  return render();
};
